/**
 * Contracts for module "connectors" (owner: ads): ad-platform, local and search connections, agency Meta BM /
 * Google MCC mapping with client consent, performance ingestion + reports, Ads Optimiser proposals and their
 * approval-gated execution envelope, Google Business Profile locations/posts/insights, review inbox + AI reply
 * drafts, Search Console / Bing Webmaster.
 * The provider modules (ads-meta, ads-google, ads-linkedin, ads-tiktok, ads-snap, gbp, reputation) re-export their
 * provider id and scope list from here; the shared engine lives at /v1/connectors (see docs/status/ads.md).
 */
import { z } from 'zod';

export const ADS_PROVIDERS = [
  'meta', 'google_ads', 'linkedin', 'tiktok', 'snap', 'gbp', 'google_search_console', 'bing_webmaster',
  // Google Analytics 4 (web analytics; flag connectors.ga4, contracts in ./connectors-ga4.ts)
  'ga4',
] as const;
export const AdsProviderSchema = z.enum(ADS_PROVIDERS);
export type AdsProvider = z.infer<typeof AdsProviderSchema>;

/** Providers that report paid performance (spend, clicks, conversions). */
export const PAID_PROVIDERS = ['meta', 'google_ads', 'linkedin', 'tiktok', 'snap'] as const satisfies readonly AdsProvider[];
export type PaidProvider = (typeof PAID_PROVIDERS)[number];
/** Providers the Ads Optimiser may propose changes for (all execution requires an approval). */
export const OPTIMISABLE_PROVIDERS = ['meta', 'google_ads', 'linkedin'] as const satisfies readonly AdsProvider[];
export const SEARCH_PROVIDERS = ['google_search_console', 'bing_webmaster'] as const satisfies readonly AdsProvider[];
/** Providers with an agency (manager) level credential: Meta Business Manager, Google Ads MCC. */
export const AGENCY_PROVIDERS = ['meta', 'google_ads', 'gbp'] as const satisfies readonly AdsProvider[];

/**
 * Least-privilege scopes requested per provider. A grant missing any of these is refused (no partial connect).
 * READ-ONLY (owner decision 30 Sep 2026): no write scope is requested (ads_management and rw_ads were removed). adwords,
 * business.manage and snapchat-marketing-api are the only scopes those providers offer and have no read-only variant;
 * connector-scopes.ts classifies every scope and refuses anything else at authorize time.
 */
export const REQUIRED_SCOPES: Record<AdsProvider, readonly string[]> = {
  meta: ['ads_read'],
  google_ads: ['https://www.googleapis.com/auth/adwords'],
  linkedin: ['r_ads', 'r_ads_reporting'],
  tiktok: ['ads.read', 'reporting.read'],
  snap: ['snapchat-marketing-api'],
  gbp: ['https://www.googleapis.com/auth/business.manage'],
  google_search_console: ['https://www.googleapis.com/auth/webmasters.readonly'],
  bing_webmaster: ['webmaster.read'],
  ga4: ['https://www.googleapis.com/auth/analytics.readonly'],
};
/** Extra scope for agency (Business Manager) connections. */
export const AGENCY_EXTRA_SCOPES: Partial<Record<AdsProvider, readonly string[]>> = { meta: ['business_management'] };

export const ConnectionStatusSchema = z.enum(['connected', 'reconnect_needed', 'not_available', 'access_removed', 'revoked']);
export type ConnectionStatus = z.infer<typeof ConnectionStatusSchema>;

const Ref = z.string().min(1).max(200).regex(/^[A-Za-z0-9:._/@+-]+$/);
const Id = z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/);
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const OAuthStartSchema = z.object({
  provider: AdsProviderSchema,
  /** reconnect an existing connection (same account, new token) */
  reconnectConnectionId: Id.optional(),
});
export type OAuthStart = z.infer<typeof OAuthStartSchema>;

/**
 * Path of the OAuth redirect URI registered with every connector provider. Served by the qm-portal worker on the
 * customer host (it 303s to the SPA screen, which posts state + code to /v1/connectors/oauth/callback). Shared so
 * the api's redirect_uri and the worker route can never drift apart.
 */
export const CONNECTORS_OAUTH_CALLBACK_PATH = '/bff/oauth/connectors/callback';

export const OAuthCallbackSchema = z.object({ state: Id, code: z.string().min(1).max(2048) });
export type OAuthCallback = z.infer<typeof OAuthCallbackSchema>;

export const ProviderAccountSchema = z.object({
  ref: z.string(),
  name: z.string().nullable(),
  currency: z.string().nullable(),
  isTest: z.boolean(),
  /** GBP / search: verified by the provider */
  verified: z.boolean(),
});
export type ProviderAccount = z.infer<typeof ProviderAccountSchema>;

export const SelectAccountsSchema = z.object({ pendingId: Id, accountRefs: z.array(Ref).min(1).max(100) });
export type SelectAccounts = z.infer<typeof SelectAccountsSchema>;

export const ConnectionViewSchema = z.object({
  id: z.string(),
  provider: AdsProviderSchema,
  accountRef: z.string(),
  displayName: z.string().nullable(),
  currency: z.string().nullable(),
  /** vault reference only; the token is never returned */
  credentialRef: z.string(),
  source: z.enum(['direct', 'agency']),
  scopes: z.array(z.string()),
  tokenExpiresAt: z.string().nullable(),
  status: ConnectionStatusSchema,
  statusReason: z.string().nullable(),
  lastSyncedAt: z.string().nullable(),
  lastSuccessAt: z.string().nullable(),
  action: z.enum(['none', 'reconnect', 'wait']),
  createdAt: z.string(),
});
export type ConnectionView = z.infer<typeof ConnectionViewSchema>;

// ------------------------------------------------------------------ performance

export const PerformanceQuerySchema = z.object({
  provider: z.enum(PAID_PROVIDERS).optional(),
  connectionId: Id.optional(),
  from: IsoDate,
  to: IsoDate,
});
export type PerformanceQuery = z.infer<typeof PerformanceQuerySchema>;

export const PerformanceTotalsSchema = z.object({
  spendMinor: z.number(),
  impressions: z.number(),
  clicks: z.number(),
  conversions: z.number(),
  leads: z.number(),
  conversations: z.number(),
  revenueMinor: z.number(),
  /** cost per lead in minor units; null when there are no leads */
  cplMinor: z.number().nullable(),
  /** revenue / spend; null when spend is 0 */
  roas: z.number().nullable(),
});

export const PerformanceReportSchema = z.object({
  currency: z.string().nullable(),
  totals: PerformanceTotalsSchema,
  byProvider: z.array(z.object({ provider: z.string(), totals: PerformanceTotalsSchema })),
  byCampaign: z.array(z.object({ provider: z.string(), campaignRef: z.string(), campaignName: z.string().nullable(), totals: PerformanceTotalsSchema })),
  freshness: z.object({
    lastSyncedAt: z.string().nullable(),
    lagMinutes: z.number().nullable(),
    /** lag <= 15 min (C09-02) */
    fresh: z.boolean(),
    /** data from a connection that is not currently connected: shown as of lastSuccessAt, never as current */
    stale: z.boolean(),
  }),
  partial: z.object({ isPartial: z.boolean(), missingDates: z.array(z.string()) }),
  connections: z.array(z.object({ id: z.string(), provider: z.string(), status: ConnectionStatusSchema, lastSuccessAt: z.string().nullable() })),
});
export type PerformanceReport = z.infer<typeof PerformanceReportSchema>;

export const LeadAttributionSchema = z.object({
  leadId: Id,
  provider: z.literal('google_ads').default('google_ads'),
  clickId: z.string().min(4).max(256).regex(/^[A-Za-z0-9_-]+$/),
  qualified: z.boolean().default(false),
});
export type LeadAttribution = z.infer<typeof LeadAttributionSchema>;

// ------------------------------------------------------------------ Ads Optimiser proposals

export const ProposalKindSchema = z.enum(['budget', 'bid', 'audience', 'negative_keywords', 'pause_campaign']);
export type ProposalKind = z.infer<typeof ProposalKindSchema>;
export const ProposalStatusSchema = z.enum(['draft', 'pending_approval', 'approved', 'rejected', 'changes_requested', 'expired', 'executing', 'applied', 'failed']);
export type ProposalStatus = z.infer<typeof ProposalStatusSchema>;

/** The provider-facing change. Amounts are minor units per day. */
export const ProposalChangeSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('set_daily_budget'), fromMinor: z.number().int(), toMinor: z.number().int() }),
  z.object({ type: z.literal('set_bid'), fromMinor: z.number().int(), toMinor: z.number().int() }),
  z.object({ type: z.literal('narrow_audience'), exclude: z.array(z.string()).min(1) }),
  z.object({ type: z.literal('add_negative_keywords'), keywords: z.array(z.object({ text: z.string(), wastedSpendMinor: z.number().int() })).min(1) }),
  z.object({ type: z.literal('pause_campaign') }),
]);
export type ProposalChange = z.infer<typeof ProposalChangeSchema>;

export const ProposalViewSchema = z.object({
  id: z.string(),
  provider: AdsProviderSchema,
  connectionId: z.string(),
  accountRef: z.string(),
  kind: ProposalKindSchema,
  campaignRef: z.string(),
  change: ProposalChangeSchema,
  reason: z.string(),
  evidence: z.record(z.string(), z.unknown()),
  spendDeltaMinorPerDay: z.number(),
  currency: z.string(),
  provenance: z.object({ engine: z.string(), version: z.string(), evidenceIds: z.array(z.string()), analysisRunId: z.string().nullable() }),
  status: ProposalStatusSchema,
  sentAt: z.string().nullable(),
  escalatedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  decidedAt: z.string().nullable(),
  appliedAt: z.string().nullable(),
  failureCode: z.string().nullable(),
  decision: z.object({ id: z.string(), decision: z.string(), actorId: z.string(), reason: z.string().nullable(), decidedAt: z.string(), policyId: z.string().nullable() }).nullable(),
  createdAt: z.string(),
});
export type ProposalView = z.infer<typeof ProposalViewSchema>;

export const ProposalDecisionSchema = z.object({
  decision: z.enum(['approve', 'reject', 'request_changes']),
  reason: z.string().trim().min(1).max(1000),
});
export type ProposalDecision = z.infer<typeof ProposalDecisionSchema>;

/** Tool call used by the Ads Optimiser agent. Without a valid approvalId the envelope refuses it. */
export const ExecuteChangeToolSchema = z.object({ proposalId: Id, approvalId: Id.optional() });
export type ExecuteChangeTool = z.infer<typeof ExecuteChangeToolSchema>;

export const RunOptimiserSchema = z.object({ connectionId: Id });

export const AutoApproveRuleSchema = z.object({
  provider: z.enum(OPTIMISABLE_PROVIDERS),
  maxDeltaMinorPerDay: z.number().int().min(0).max(1_000_000),
  currency: z.string().length(3),
});
export type AutoApproveRule = z.infer<typeof AutoApproveRuleSchema>;

// ------------------------------------------------------------------ agency

export const ConsentGrantSchema = z.object({ partnerId: Id, provider: z.enum(AGENCY_PROVIDERS) });
export type ConsentGrant = z.infer<typeof ConsentGrantSchema>;

export const AgencyMappingSchema = z.object({ agencyConnectionId: Id, accountRef: Ref, clientTenantId: Id });
export type AgencyMapping = z.infer<typeof AgencyMappingSchema>;

export const SendProposalsSchema = z.object({ proposalIds: z.array(Id).min(1).max(200) });

// ------------------------------------------------------------------ GBP

export const SelectLocationsSchema = z.object({ connectionId: Id, locationRefs: z.array(Ref).min(1).max(500) });

export const GbpPostCreateSchema = z.object({
  locationId: Id,
  kind: z.enum(['update', 'offer', 'event']).default('update'),
  body: z.string().trim().min(1).max(1500),
  offer: z.object({ couponCode: z.string().max(58).optional(), redeemUrl: z.string().url().optional(), terms: z.string().max(5000).optional() }).optional(),
  locale: z.enum(['en', 'ar']).default('en'),
  scheduledFor: z.string().datetime({ offset: true }),
});
export type GbpPostCreate = z.infer<typeof GbpPostCreateSchema>;

export const GbpPostStatusSchema = z.enum(['draft', 'pending_approval', 'scheduled', 'publishing', 'published', 'rejected_by_google', 'rejected', 'failed']);
export type GbpPostStatus = z.infer<typeof GbpPostStatusSchema>;

export const PostDecisionSchema = z.object({ decision: z.enum(['approve', 'reject']), reason: z.string().trim().min(1).max(1000) });

// ------------------------------------------------------------------ reviews

export const ReviewQuerySchema = z.object({
  minRating: z.coerce.number().int().min(1).max(5).optional(),
  maxRating: z.coerce.number().int().min(1).max(5).optional(),
  status: z.enum(['new', 'replied', 'deleted']).optional(),
  flagged: z.enum(['true', 'false']).optional(),
  locationId: Id.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const ReplyDecisionSchema = z.object({ decision: z.enum(['approve', 'reject']), reason: z.string().trim().min(1).max(1000), text: z.string().trim().min(1).max(4000).optional() });

/** Reviews at or below this rating are flagged for attention. */
export const REVIEW_FLAG_MAX_RATING = 3;

// ------------------------------------------------------------------ jobs (qm-jobs cron / integrations queue)

export const CONNECTOR_JOB_TYPES = {
  healthRun: 'connectors.health.run',
  performanceSync: 'connectors.performance.sync',
  backfill: 'connectors.performance.backfill',
  proposalLifecycle: 'connectors.proposals.lifecycle',
  gbpPublishDue: 'connectors.gbp.publish_due',
  reviewSync: 'connectors.reviews.sync',
  searchSync: 'connectors.search.sync',
} as const;

/** Detection target for broken connections (C10-03) and the health cadence it assumes. */
export const HEALTH_DETECTION_TARGET_MIN = 15;
export const HEALTH_INTERVAL_MIN = 5;
/** Freshness target (C09-02). */
export const FRESHNESS_TARGET_MIN = 15;
/** Approval SLA (C07-05) and proposal lifetime (QM-A04-002). */
export const APPROVAL_REMINDER_H = 24;
export const APPROVAL_ESCALATION_H = 48;
export const PROPOSAL_EXPIRY_D = 7;

// ------------------------------------------------------------------ controlled browser fallback (QM-D20-012)

/** Actions allowed by default when no official API exists (read-only). */
export const BROWSER_SAFE_ACTIONS = ['navigate', 'read', 'screenshot', 'scroll', 'wait'] as const;
export const BrowserActionSchema = z.enum(['navigate', 'read', 'screenshot', 'scroll', 'wait', 'click', 'type', 'submit']);
const Origin = z.string().url().max(300).refine((u) => new URL(u).origin === u, 'must be an origin (scheme://host[:port])');

export const BrowserConsentSchema = z.object({
  origins: z.array(Origin).min(1).max(20),
  purpose: z.string().trim().min(3).max(200),
  expiresAt: z.string().datetime({ offset: true }),
});
export type BrowserConsent = z.infer<typeof BrowserConsentSchema>;

export const BrowserStepSchema = z.object({
  action: BrowserActionSchema,
  url: z.string().url().max(2000),
  target: z.object({
    tag: z.string().max(40).optional(), type: z.string().max(40).optional(), name: z.string().max(120).optional(),
    formKind: z.enum(['login', 'payment', 'other']).optional(),
  }).optional(),
  estMinutes: z.number().min(0).max(60).optional(),
});
export type BrowserStep = z.infer<typeof BrowserStepSchema>;

export const BrowserTaskSchema = z.object({
  consentId: Id,
  purpose: z.string().trim().min(3).max(200),
  allowlist: z.array(z.object({ action: BrowserActionSchema, origin: Origin })).min(1).max(50),
  spendCapMinutes: z.number().min(0.1).max(120),
  steps: z.array(BrowserStepSchema).min(1).max(100),
});
export type BrowserTask = z.infer<typeof BrowserTaskSchema>;

// ------------------------------------------------------------------ Quixo Audit connected data (qm-audit -> qm-api, internal, read-only)
//
// A customer may OPT IN to let their Quixo Audit read summaries of accounts they already connected in the portal.
// qm-audit never imports this module: it calls POST /v1/connectors/internal/audit/{check,summaries} over the API
// service binding as the system actor (x-qm-internal-token) with the job's tenant id. Only ids travel: a connection
// id per provider, never a token or a credential reference. The summaries come from data the connectors already
// synced (no provider call during an audit); stale or missing data is reported as such, never as a number.

/** Providers an audit can read, in report order. Social = the Meta (Facebook/Instagram), LinkedIn, TikTok, Snap ad accounts. */
export const AUDIT_CONNECTED_PROVIDERS = ['google_search_console', 'bing_webmaster', 'gbp', 'google_ads', 'meta', 'linkedin', 'tiktok', 'snap'] as const satisfies readonly AdsProvider[];
export type AuditConnectedProvider = (typeof AUDIT_CONNECTED_PROVIDERS)[number];
export const AuditConnectedProviderSchema = z.enum(AUDIT_CONNECTED_PROVIDERS);

/** What the customer chose for one provider: the connection id only. */
export const AuditConnectionRefSchema = z.object({ provider: AuditConnectedProviderSchema, connectionId: Id }).strict();
export type AuditConnectionRef = z.infer<typeof AuditConnectionRefSchema>;

/** At most one connection per provider. */
export const AuditConnectionRefsSchema = z.array(AuditConnectionRefSchema).max(AUDIT_CONNECTED_PROVIDERS.length)
  .refine((a) => new Set(a.map((r) => r.provider)).size === a.length, { message: 'One connection per provider' });

/** POST /v1/connectors/internal/audit/check - every id must be a live connection of this tenant for that provider, else 404. */
export const AuditConnectionsCheckRequestSchema = z.object({ tenantId: Id, connections: AuditConnectionRefsSchema.refine((a) => a.length > 0, { message: 'At least one connection' }) }).strict();
export type AuditConnectionsCheckRequest = z.infer<typeof AuditConnectionsCheckRequestSchema>;

/** POST /v1/connectors/internal/audit/summaries - read-only summaries for the window (inclusive dates). */
export const AuditConnectedSummariesRequestSchema = AuditConnectionsCheckRequestSchema.extend({ from: IsoDate, to: IsoDate }).strict();
export type AuditConnectedSummariesRequest = z.infer<typeof AuditConnectedSummariesRequestSchema>;

/** Metric keys a summary may carry (the report has a label for each). Money is minor units + `currency`. */
export const AUDIT_CONNECTED_METRICS = [
  'clicks', 'impressions', 'views', 'calls', 'directionRequests', 'websiteClicks', 'reviewCount', 'averageRating', 'spendMinor', 'leads', 'conversions',
] as const;
export const AuditConnectedMetricSchema = z.enum(AUDIT_CONNECTED_METRICS);
export type AuditConnectedMetric = z.infer<typeof AuditConnectedMetricSchema>;

/**
 * Why a provider has no figures: `reconnect_needed` (access broken), `revoked`, `not_available` (provider not
 * approved for this account), `no_data` (connected, nothing synced for the window yet), `unavailable` (could not be read during the audit).
 */
export const AuditConnectedReasonSchema = z.enum(['reconnect_needed', 'revoked', 'not_available', 'no_data', 'unavailable']);
export type AuditConnectedReason = z.infer<typeof AuditConnectedReasonSchema>;

export const AuditProviderSummarySchema = z.object({
  provider: AuditConnectedProviderSchema,
  connectionId: Id,
  status: z.enum(['measured', 'not_measured']),
  reason: AuditConnectedReasonSchema.nullable(),
  /** what the figures describe: a verified site URL, a location title, an ad account name */
  label: z.string().max(300).nullable(),
  /** last successful sync the figures come from */
  asOf: z.string().nullable(),
  from: IsoDate,
  to: IsoDate,
  currency: z.string().max(8).nullable(),
  /** only measured values; an empty list with status not_measured means "no figures", never zero */
  metrics: z.array(z.object({ key: AuditConnectedMetricSchema, value: z.number().finite() })).max(AUDIT_CONNECTED_METRICS.length),
  /** search: top queries by clicks (at most 5) */
  topQueries: z.array(z.object({ query: z.string().max(500), clicks: z.number().int().nonnegative(), impressions: z.number().int().nonnegative() })).max(5),
});
export type AuditProviderSummary = z.infer<typeof AuditProviderSummarySchema>;

export const AuditConnectedSummariesResponseSchema = z.object({ data: z.object({ summaries: z.array(AuditProviderSummarySchema) }) });

// ------------------------------------------------------------------ agency ads overview (all clients, read-only)

/** Feature flag for GET /v1/connectors/agency/overview. Registered OFF (core 0978); evaluated per partner. */
export const AGENCY_OVERVIEW_FLAG = 'connectors.agency_overview';
/** Grant scope the agency needs on a client's active partner_client_grants row to see that client's ad figures. */
export const AGENCY_OVERVIEW_SCOPE = 'read:reports';
/** Longest range one overview request may cover (days, inclusive). */
export const AGENCY_OVERVIEW_MAX_DAYS = 366;

/** Daily metric a network reports as its "result". The first one with a non-zero value in either period wins. */
export const OVERVIEW_RESULT_METRICS = ['leads', 'conversions', 'conversations'] as const;
export type OverviewResultMetric = (typeof OVERVIEW_RESULT_METRICS)[number];

/**
 * Networks the overview reads, as data: adding a network (TikTok, Snap, LinkedIn) is one entry here plus the label
 * key `connectors.agencyOverview.network.<id>`. Its synced rows in ads_metrics_daily and its agency mappings are
 * then picked up with no new screen and no new query.
 */
export const AGENCY_OVERVIEW_NETWORKS: Readonly<Partial<Record<(typeof PAID_PROVIDERS)[number], { results: readonly OverviewResultMetric[] }>>> = {
  meta: { results: ['leads', 'conversations', 'conversions'] },
  google_ads: { results: ['conversions', 'leads'] },
};
export const agencyOverviewNetworks = (): (typeof PAID_PROVIDERS)[number][] => PAID_PROVIDERS.filter((p) => AGENCY_OVERVIEW_NETWORKS[p]);

/** Flag thresholds (documented in docs/status/ads.md). */
export const OVERVIEW_SPIKE_RATIO = 2;
export const OVERVIEW_SPIKE_BASELINE_DAYS = 7;
export const OVERVIEW_ZERO_DELIVERY_DAYS = 2;
export const OVERVIEW_ZERO_DELIVERY_BASELINE_DAYS = 14;
export const OVERVIEW_PACING_BAND = 0.25;
export const OVERVIEW_PACING_RECENT_DAYS = 7;

export const AgencyOverviewQuerySchema = z.object({
  from: IsoDate,
  to: IsoDate,
  network: z.string().max(32).optional(),
  clientTenantId: z.string().min(1).max(128).regex(/^[A-Za-z0-9_:.-]+$/).optional(),
});
export type AgencyOverviewQuery = z.infer<typeof AgencyOverviewQuerySchema>;

export const OVERVIEW_FLAG_CODES = ['disconnected', 'zero_delivery', 'spend_spike', 'pending_approvals'] as const;
export type OverviewFlagCode = (typeof OVERVIEW_FLAG_CODES)[number];
export const OVERVIEW_PACING_STATES = ['ahead', 'on_track', 'behind', 'no_baseline'] as const;
export type OverviewPacingState = (typeof OVERVIEW_PACING_STATES)[number];

const OverviewMetricsSchema = z.object({
  spendMinor: z.number().nullable(),
  impressions: z.number(),
  clicks: z.number(),
  results: z.number(),
  revenueMinor: z.number().nullable(),
  /** spend / results; null without results or when currencies are mixed */
  cpaMinor: z.number().nullable(),
  /** revenue / spend; null when no revenue is reported */
  roas: z.number().nullable(),
});
export type OverviewMetrics = z.infer<typeof OverviewMetricsSchema>;

/** Change vs the previous period of the same length, as a fraction (0.25 = +25%); null when there is no baseline. */
const OverviewChangeSchema = z.object({ spend: z.number().nullable(), results: z.number().nullable(), cpa: z.number().nullable(), roas: z.number().nullable() });

/** Spend pacing for the range against the previous period (budgets are not synced; see docs/status/ads.md). */
const OverviewPacingSchema = z.object({
  state: z.enum(OVERVIEW_PACING_STATES),
  /** spend so far + recent daily run rate x days left in the range */
  projectedMinor: z.number().nullable(),
  /** projected / previous spend - 1 */
  vsPrevious: z.number().nullable(),
});

const OverviewFlagSchema = z.object({ code: z.enum(OVERVIEW_FLAG_CODES), count: z.number().int().positive().optional() });

export const OverviewAccountRowSchema = z.object({
  connectionId: z.string(),
  network: z.string(),
  accountRef: z.string(),
  accountName: z.string().nullable(),
  currency: z.string().nullable(),
  status: z.string(),
  lastSyncedAt: z.string().nullable(),
  resultMetric: z.enum(OVERVIEW_RESULT_METRICS),
  current: OverviewMetricsSchema,
  previous: OverviewMetricsSchema,
  change: OverviewChangeSchema,
  pacing: OverviewPacingSchema,
  flags: z.array(OverviewFlagSchema),
});
export type OverviewAccountRow = z.infer<typeof OverviewAccountRowSchema>;

export const OverviewClientRowSchema = z.object({
  clientTenantId: z.string(),
  clientName: z.string().nullable(),
  /** one currency across the client's accounts, 'MIXED' when they differ (money totals are then null), null with no accounts */
  currency: z.string().nullable(),
  networks: z.array(z.string()),
  current: OverviewMetricsSchema,
  previous: OverviewMetricsSchema,
  change: OverviewChangeSchema,
  pacing: OverviewPacingSchema,
  flags: z.array(OverviewFlagSchema),
  accounts: z.array(OverviewAccountRowSchema),
});
export type OverviewClientRow = z.infer<typeof OverviewClientRowSchema>;

export const AgencyOverviewSchema = z.object({
  range: z.object({ from: IsoDate, to: IsoDate, previousFrom: IsoDate, previousTo: IsoDate, days: z.number().int().positive() }),
  /** networks this overview can show (filter options), from AGENCY_OVERVIEW_NETWORKS */
  networks: z.array(z.string()),
  clients: z.array(OverviewClientRowSchema),
});
export type AgencyOverview = z.infer<typeof AgencyOverviewSchema>;

// ---------------------------------------------------------------- agency view of TikTok, Snap and LinkedIn Ads
/**
 * Partner-portal read routes served by /v1/ads-{tiktok,snap,linkedin}/agency/... . A client is reachable only through
 * an ACTIVE partner_client_grants row carrying `read:reports` (non-admin agency staff: assigned clients only);
 * anything else is 404. Global flag, registered OFF (staff create/enable it in the flags console).
 *
 * What the backend supports for these networks today: the CLIENT connects its own ad account (direct OAuth on the
 * customer host), the account syncs daily into ads_metrics_daily, and reports read from there. There is no agency
 * (manager-level) credential for these networks (AGENCY_PROVIDERS), so an agency cannot connect a TikTok Business
 * Center, Snap organisation or LinkedIn ad account itself, and cannot map accounts to clients.
 */
export const ADS_AGENCY_NETWORKS_FLAG = 'connectors.agency_networks';
export const AGENCY_NETWORK_PROVIDERS = ['tiktok', 'snap', 'linkedin'] as const satisfies readonly AdsProvider[];
export type AgencyNetworkProvider = (typeof AGENCY_NETWORK_PROVIDERS)[number];
/** Read by the portal to show only what the backend supports. */
export const AGENCY_NETWORK_CAPABILITIES = { clientConnect: true, connectStatus: true, performance: true, agencyConnect: false, accountMapping: false } as const;
/** Grant scope the agency needs on the client (same scope as the agency KPI screens). */
export const AGENCY_NETWORK_READ_SCOPE = 'read:reports';
/** Longest report window, in days. */
export const AGENCY_NETWORK_MAX_DAYS = 366;

export const AgencyNetworkReportQuerySchema = z.object({ from: IsoDate, to: IsoDate })
  .refine((q) => q.from <= q.to, { message: 'from must be on or before to', path: ['from'] })
  .refine((q) => (Date.parse(`${q.to}T00:00:00Z`) - Date.parse(`${q.from}T00:00:00Z`)) / 86_400_000 < AGENCY_NETWORK_MAX_DAYS, { message: 'window too long', path: ['to'] });
export type AgencyNetworkReportQuery = z.infer<typeof AgencyNetworkReportQuerySchema>;

export const AgencyNetworkConnectionSchema = z.object({
  id: z.string(), accountRef: z.string(), displayName: z.string().nullable(), currency: z.string().nullable(), status: ConnectionStatusSchema,
  statusReason: z.string().nullable(), lastSyncedAt: z.string().nullable(), lastSuccessAt: z.string().nullable(),
});
export type AgencyNetworkConnection = z.infer<typeof AgencyNetworkConnectionSchema>;

export const AgencyNetworkClientSchema = z.object({
  clientTenantId: z.string(), clientName: z.string().nullable(), connections: z.array(AgencyNetworkConnectionSchema),
});
export type AgencyNetworkClient = z.infer<typeof AgencyNetworkClientSchema>;

export const AgencyNetworkClientsSchema = z.object({
  provider: z.enum(AGENCY_NETWORK_PROVIDERS),
  capabilities: z.object({ clientConnect: z.boolean(), connectStatus: z.boolean(), performance: z.boolean(), agencyConnect: z.boolean(), accountMapping: z.boolean() }),
  clients: z.array(AgencyNetworkClientSchema),
});
export type AgencyNetworkClients = z.infer<typeof AgencyNetworkClientsSchema>;

const NetTotals = z.object({
  currency: z.string(), spendMinor: z.number().int(), impressions: z.number().int(), clicks: z.number().int(), conversions: z.number(), leads: z.number().int(),
  cplMinor: z.number().int().nullable(), cpcMinor: z.number().int().nullable(), ctr: z.number().nullable(),
});
export type AgencyNetworkTotals = z.infer<typeof NetTotals>;

export const AgencyNetworkReportSchema = z.object({
  provider: z.enum(AGENCY_NETWORK_PROVIDERS),
  clientTenantId: z.string(), clientName: z.string().nullable(), from: IsoDate, to: IsoDate,
  /** One row per currency: spend is never summed across currencies. */
  totals: z.array(NetTotals),
  byCampaign: z.array(NetTotals.extend({ connectionId: z.string(), accountRef: z.string(), campaignRef: z.string(), campaignName: z.string().nullable() })),
  connections: z.array(AgencyNetworkConnectionSchema),
  freshness: z.object({ lastSyncedAt: z.string().nullable(), stale: z.boolean() }),
  partial: z.object({ isPartial: z.boolean(), missingDates: z.array(IsoDate) }),
});
export type AgencyNetworkReport = z.infer<typeof AgencyNetworkReportSchema>;
